import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

export const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export const THEME_BG = { light: 0xf6f6f4, dark: 0x141416 };

const easeOutCubic = (k) => 1 - Math.pow(1 - k, 3);

export function createEngine(mount, { background, theme = "light", controls: controlsType = "orbit" } = {}) {
  const w = mount.clientWidth || 800, h = mount.clientHeight || 600;
  const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(w, h);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.92;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor(background !== undefined ? background : (THEME_BG[theme] || THEME_BG.light), 1);
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000000);
  camera.position.set(120, 90, 160);
  let controls;
  if (controlsType === "trackball") {
    // free 360° tumbling (no polar/pole limits)
    controls = new TrackballControls(camera, renderer.domElement);
    controls.rotateSpeed = 2.2;
    controls.zoomSpeed = 1.0;
    controls.panSpeed = 0.4;
    controls.staticMoving = true; // no momentum/coast — rotation stops on release (Fusion-like)
  } else {
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
  }

  // realistic image-based lighting (soft studio reflections) + a shadow-casting key light
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.add(new THREE.AmbientLight(0xffffff, 0.55));
  const key = new THREE.DirectionalLight(0xffffff, 1.35);
  key.position.set(1, 1.6, 1);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.radius = 4;
  key.shadow.normalBias = 0.6;
  scene.add(key); scene.add(key.target);
  const fill = new THREE.DirectionalLight(0xffffff, 0.35); fill.position.set(-1, -0.4, -1); scene.add(fill);

  // ground plane that only shows the contact shadow (invisible otherwise)
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShadowMaterial({ opacity: 0.32, transparent: true, depthWrite: false }));
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  ground.renderOrder = -1;
  scene.add(ground);

  const engine = { renderer, scene, camera, controls, controlsType, parts: [], center: new THREE.Vector3(), radius: 50, mount, theme, onFrame: null, _raf: null, _tween: null, _key: key, _ground: ground };
  const animate = () => {
    engine._raf = requestAnimationFrame(animate);
    if (engine._tween) engine._tween(performance.now());
    if (!engine._suspendControls) controls.update();
    if (engine._extras) { const tt = performance.now(); for (const o of engine._extras) { if (o.userData && o.userData.anno) o.scale.setScalar(1 + 0.05 * Math.sin(tt * 0.004 + (o.userData.phase || 0))); } }
    renderer.render(scene, camera);
    if (engine._gizmo) _renderGizmo(engine);
    if (engine.onFrame) engine.onFrame();
  };
  animate();
  const onResize = () => { const nw = mount.clientWidth, nh = mount.clientHeight; if (!nw || !nh) return; renderer.setSize(nw, nh); camera.aspect = nw / nh; camera.updateProjectionMatrix(); controls.handleResize && controls.handleResize(); };
  window.addEventListener("resize", onResize);
  engine._onResize = onResize;
  engine.resize = onResize;
  engine._pmrem = pmrem;
  return engine;
}

// Switch light/dark scene theme: background + edge line contrast.
export function setSceneTheme(engine, theme) {
  if (!engine) return;
  engine.theme = theme;
  engine.renderer.setClearColor(THEME_BG[theme] || THEME_BG.light, 1);
  engine.parts.forEach((p) => p.outer.traverse((o) => {
    if (o.isLineSegments) { o.material.color.set(0x2b2b2b); o.material.opacity = 0.24; o.material.needsUpdate = true; }
  }));
}

export function disposeEngine(engine) {
  if (!engine) return;
  cancelAnimationFrame(engine._raf);
  window.removeEventListener("resize", engine._onResize);
  engine._navCleanup && engine._navCleanup();
  disableViewCube(engine);
  engine._pmrem && engine._pmrem.dispose();
  engine.controls.dispose();
  engine.renderer.dispose();
  const el = engine.renderer.domElement;
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

// Constrained turntable rotate: azimuth around the up-axis, pitch around a stable
// horizontal axis. The camera sweeps a full great circle (over the top / under the
// bottom) with camera.up pinned to the up-axis, so the horizon stays level and there
// are NO pole "walls" — exactly like Fusion 360's Constrained Orbit.
function orbitConstrained(engine, upAxis, dTheta, dPhi) {
  const cam = engine.camera, tgt = engine.controls.target;
  const offset = cam.position.clone().sub(tgt);
  if (dTheta) offset.applyAxisAngle(upAxis, -dTheta);                // azimuth around up (follows cursor)
  let right = new THREE.Vector3().crossVectors(upAxis, offset);      // stable horizontal axis
  if (right.lengthSq() < 1e-8) right = (engine._orbitRight || new THREE.Vector3(1, 0, 0)).clone();
  right.normalize();
  engine._orbitRight = right.clone();
  if (dPhi) offset.applyAxisAngle(right, -dPhi);                     // pitch over the poles (no clamp)
  cam.position.copy(tgt).add(offset);
  cam.up.copy(upAxis);
  cam.lookAt(tgt);
}

// Fusion 360-style navigation: MMB = pan, Shift+MMB = orbit, scroll = zoom.
// Left mouse stays free for selection (face/point picking in the studio).
export function applyFusionNav(engine) {
  const c = engine.controls;
  if (engine.controlsType === "trackball") {
    // TrackballControls: disable LEFT, MMB pans; holding Shift forces free-orbit on drag.
    c.mouseButtons = { LEFT: -1, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    c.keys = ["ShiftLeft", "KeyZ", "KeyX"]; // keys[0] (Shift) => rotate
    engine._navCleanup = () => {};
    return;
  }
  // OrbitControls handles PAN (middle drag) + zoom-to-cursor. Rotation is CUSTOM
  // (constrained turntable) so the camera tumbles continuously over the top and under
  // the bottom with a level horizon — like Fusion 360 (no invisible pole "walls").
  c.mouseButtons = { LEFT: null, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
  c.screenSpacePanning = true;
  c.zoomToCursor = true;
  c.enableDamping = false; // no coast/momentum (Fusion-like); avoids fighting manual rotation
  const dom = engine.renderer.domElement;
  const ROT = 0.9;
  let rotating = false, lx = 0, ly = 0;
  const upAxisOf = () => {
    const fv = engine._frontView;
    return fv && fv.up ? new THREE.Vector3(fv.up[0], fv.up[1], fv.up[2]).normalize() : new THREE.Vector3(0, 1, 0);
  };
  const onDown = (e) => {
    if (c.enabled === false) return;             // nav disabled by studio (e.g. hotspot mode)
    if (e.button === 1 && e.shiftKey) {          // Shift + middle-mouse = orbit
      rotating = true; lx = e.clientX; ly = e.clientY;
      c.enabled = false; engine._suspendControls = true; engine._orbitRight = null;
      try { dom.setPointerCapture(e.pointerId); } catch (_) { /* best-effort: pointer id may already be released */ }
      e.preventDefault(); e.stopPropagation();
    }
  };
  const onMove = (e) => {
    if (!rotating) return;
    const h = dom.clientHeight || 600;
    const dTheta = (2 * Math.PI * (e.clientX - lx) / h) * ROT;
    const dPhi = (2 * Math.PI * (e.clientY - ly) / h) * ROT;
    lx = e.clientX; ly = e.clientY;
    orbitConstrained(engine, upAxisOf(), dTheta, dPhi);
    e.preventDefault();
  };
  const onUp = (e) => {
    if (!rotating) return;
    rotating = false; c.enabled = true; engine._suspendControls = false;
    try { dom.releasePointerCapture(e.pointerId); } catch (_) { /* best-effort: capture may already be gone */ }
  };
  dom.addEventListener("pointerdown", onDown, true);
  window.addEventListener("pointermove", onMove, true);
  window.addEventListener("pointerup", onUp, true);
  engine._navCleanup = () => {
    dom.removeEventListener("pointerdown", onDown, true);
    window.removeEventListener("pointermove", onMove, true);
    window.removeEventListener("pointerup", onUp, true);
  };
}

export function parseModel(ext, data) {
  if (ext === "stl") return new THREE.Mesh(new STLLoader().parse(data));
  return new OBJLoader().parse(typeof data === "string" ? data : new TextDecoder().decode(data));
}

// Solid shaded part centered at local origin, wrapped in an `outer` group we transform.
export function buildPart(root, color) {
  root.updateMatrixWorld(true);
  const outer = new THREE.Group();
  const content = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color || "#d9d9d9"), metalness: 0.05, roughness: 0.68, envMapIntensity: 0.55, side: THREE.DoubleSide });
  root.traverse((child) => {
    if (child.isMesh && child.geometry) {
      const g = child.geometry.clone().applyMatrix4(child.matrixWorld);
      g.computeVertexNormals();
      const mesh = new THREE.Mesh(g, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.selectable = true;
      content.add(mesh);
      content.add(new THREE.LineSegments(new THREE.EdgesGeometry(g, 32), new THREE.LineBasicMaterial({ color: 0x2b2b2b, transparent: true, opacity: 0.22 })));
    }
  });
  const box = new THREE.Box3().setFromObject(content);
  const c = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  content.position.set(-c.x, -c.y, -c.z);
  outer.add(content);
  const radius = Math.max(size.x, size.y, size.z) / 2 || 1;
  return { outer, mat, radius };
}

export function applyTransform(outer, t) {
  if (!t) return;
  outer.position.set(t.px || 0, t.py || 0, t.pz || 0);
  outer.rotation.set(THREE.MathUtils.degToRad(t.rx || 0), THREE.MathUtils.degToRad(t.ry || 0), THREE.MathUtils.degToRad(t.rz || 0));
  if (t.visible !== undefined) outer.visible = t.visible;
}

export function fitAll(engine, setCam = true) {
  if (!engine || !engine.parts.length) return;
  const box = new THREE.Box3();
  engine.parts.forEach((p) => { if (p.outer.visible) box.expandByObject(p.outer); });
  if (box.isEmpty()) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.y, size.z) || 1;
  engine.center = center; engine.radius = radius;
  engine._box = box.clone();
  engine.camera.near = radius / 1000; engine.camera.far = radius * 2000; engine.camera.updateProjectionMatrix();
  updateShadow(engine);
  if (setCam) {
    const d = radius * 2.2;
    engine.camera.position.set(center.x + d * 0.7, center.y + d * 0.55, center.z + d);
    engine.controls.target.copy(center); engine.controls.update();
  }
}

// Orient the ground plane + shadow-casting light on the user-selected front/bottom
// so the plane sits under the real "bottom" and the shadow falls from front to back.
function updateShadow(engine) {
  const k = engine && engine._key;
  if (!k || !engine.center) return;
  const center = engine.center, radius = engine.radius || 50;
  const fv = engine._frontView;
  const upAxis = fv && fv.up ? new THREE.Vector3(fv.up[0], fv.up[1], fv.up[2]).normalize() : new THREE.Vector3(0, 1, 0);
  const front = fv && fv.d ? new THREE.Vector3(fv.d[0], fv.d[1], fv.d[2]) : new THREE.Vector3(0, 0, 1);
  front.addScaledVector(upAxis, -front.dot(upAxis)); // horizontal relative to the real ground
  if (front.lengthSq() < 1e-6) front.set(0, 0, 1);
  front.normalize();
  const right = new THREE.Vector3().crossVectors(upAxis, front).normalize();

  // ground plane: normal = upAxis, positioned at the model's lowest extent along -upAxis
  const g = engine._ground, box = engine._box;
  if (g && box) {
    let minD = Infinity;
    const xs = [box.min.x, box.max.x], ys = [box.min.y, box.max.y], zs = [box.min.z, box.max.z];
    for (const x of xs) for (const y of ys) for (const z of zs) minD = Math.min(minD, new THREE.Vector3(x, y, z).dot(upAxis));
    g.position.copy(center).addScaledVector(upAxis, minD - center.dot(upAxis) - radius * 0.002);
    g.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), upAxis);
    g.scale.set(radius * 12, radius * 12, 1);
  }

  // key light: in front of + above the model (+ slight side for a diagonal shadow)
  k.position.copy(center)
    .addScaledVector(front, radius * 1.5)
    .addScaledVector(upAxis, radius * 2.2)
    .addScaledVector(right, radius * 0.5);
  k.target.position.copy(center); k.target.updateMatrixWorld();
  const cam = k.shadow.camera, s = radius * 1.7;
  cam.left = -s; cam.right = s; cam.top = s; cam.bottom = -s;
  cam.near = radius * 0.05; cam.far = radius * 8;
  cam.updateProjectionMatrix();
}

// Register the user-selected front view so the shadow orients from front to back.
export function setFrontView(engine, frontView) {
  if (!engine) return;
  engine._frontView = frontView || null;
  updateShadow(engine);
}

export function snapshotCamera(engine) {
  const p = engine.camera.position, t = engine.controls.target, u = engine.camera.up;
  return { px: p.x, py: p.y, pz: p.z, tx: t.x, ty: t.y, tz: t.z, ux: u.x, uy: u.y, uz: u.z };
}

// Animate camera + per-part transforms. transformsById: { [partId]: {px,py,pz,rx,ry,rz,visible} }
export function tweenTo(engine, cam, transformsById, duration = 850, onDone) {
  if (cam && cam.ux !== undefined) engine.camera.up.set(cam.ux, cam.uy, cam.uz);
  const startPos = engine.camera.position.clone();
  const startTgt = engine.controls.target.clone();
  const endPos = cam ? new THREE.Vector3(cam.px, cam.py, cam.pz) : startPos.clone();
  const endTgt = cam ? new THREE.Vector3(cam.tx, cam.ty, cam.tz) : startTgt.clone();
  const D2R = THREE.MathUtils.degToRad;
  const collectMats = (obj) => {
    const mats = [];
    obj.traverse((o) => { if (o.material) { const m = o.material; if (m._baseOpacity === undefined) m._baseOpacity = m.opacity; mats.push(m); } });
    return mats;
  };
  const setAlpha = (outer, mats, alpha) => {
    outer.userData._alpha = alpha;
    outer.visible = alpha > 0.001;
    mats.forEach((m) => { m.opacity = m._baseOpacity * alpha; m.transparent = m._baseOpacity < 1 || alpha < 0.999; });
  };
  const states = engine.parts.map((p) => {
    const t = transformsById && transformsById[p.id];
    const endVis = t && t.visible !== undefined ? t.visible : (p.outer.userData._alpha === undefined ? p.outer.visible : p.outer.userData._alpha > 0.001);
    const mats = collectMats(p.outer);
    const startAlpha = p.outer.userData._alpha !== undefined ? p.outer.userData._alpha : (p.outer.visible ? 1 : 0);
    const targetAlpha = endVis ? 1 : 0;
    if (targetAlpha > 0 && !p.outer.visible) p.outer.visible = true; // reveal before fading in
    const animAlpha = startAlpha !== targetAlpha;
    const appearing = animAlpha && targetAlpha > startAlpha;
    const ep = t ? new THREE.Vector3(t.px || 0, t.py || 0, t.pz || 0) : p.outer.position.clone();
    const eq = t ? new THREE.Quaternion().setFromEuler(new THREE.Euler(D2R(t.rx || 0), D2R(t.ry || 0), D2R(t.rz || 0))) : p.outer.quaternion.clone();
    // A newly-appearing part fades in on its placed spot (no slide/rotate from the default transform).
    if (appearing) { p.outer.position.copy(ep); p.outer.quaternion.copy(eq); }
    return {
      p, mats, startAlpha, targetAlpha, animAlpha,
      sp: appearing ? ep.clone() : p.outer.position.clone(),
      sq: appearing ? eq.clone() : p.outer.quaternion.clone(),
      ep, eq,
    };
  });
  const t0 = performance.now();
  const fadeOutDur = Math.min(duration, 500);
  // Parts that newly APPEAR start fading in about halfway through the camera move,
  // so they come into view while the camera is still travelling (feels quicker).
  const hasAppearing = states.some((s) => s.animAlpha && s.targetAlpha > s.startAlpha);
  const appearDelay = hasAppearing ? duration * 0.10 : 0;
  const appearFadeDur = 650;
  const totalDur = hasAppearing ? Math.max(duration, appearDelay + appearFadeDur) : duration;
  engine._tween = (now) => {
    const el = now - t0;
    const k = Math.min(1, el / duration);
    const e = easeOutCubic(k);
    engine.camera.position.lerpVectors(startPos, endPos, e);
    engine.controls.target.lerpVectors(startTgt, endTgt, e);
    states.forEach((s) => {
      s.p.outer.position.lerpVectors(s.sp, s.ep, e);
      s.p.outer.quaternion.slerpQuaternions(s.sq, s.eq, e); // shortest-path rotation (no spinning)
      if (s.animAlpha) {
        let alpha;
        if (s.targetAlpha > s.startAlpha) {
          // appearing: hold hidden until the camera is nearly in place, then fade in
          const fi = el <= appearDelay ? 0 : Math.min(1, (el - appearDelay) / appearFadeDur);
          alpha = s.startAlpha + (s.targetAlpha - s.startAlpha) * easeOutCubic(fi);
        } else {
          // disappearing: fade out together with the camera move
          const fo = Math.min(1, el / fadeOutDur);
          alpha = s.startAlpha + (s.targetAlpha - s.startAlpha) * easeOutCubic(fo);
        }
        setAlpha(s.p.outer, s.mats, alpha);
      }
    });
    if (el >= totalDur) {
      engine._tween = null;
      states.forEach((s) => setAlpha(s.p.outer, s.mats, s.targetAlpha)); // clean, correct final state for every part
      onDone && onDone();
    }
  };
}

const _mouseFromEvent = (engine, ev) => {
  const rect = engine.renderer.domElement.getBoundingClientRect();
  return new THREE.Vector2(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
};

const _visibleMeshes = (engine) => {
  const meshes = [];
  engine.parts.forEach((p) => p.outer.visible && p.outer.traverse((o) => { if (o.isMesh) meshes.push(o); }));
  return meshes;
};

export function pickPoint(engine, ev) {
  const ray = new THREE.Raycaster(); ray.setFromCamera(_mouseFromEvent(engine, ev), engine.camera);
  const hits = ray.intersectObjects(_visibleMeshes(engine), true);
  return hits.length ? hits[0].point.clone() : null;
}

// like pickPoint but also returns the world-space surface normal at the hit
export function pickHit(engine, ev) {
  const ray = new THREE.Raycaster(); ray.setFromCamera(_mouseFromEvent(engine, ev), engine.camera);
  const hits = ray.intersectObjects(_visibleMeshes(engine), true);
  if (!hits.length) return null;
  const h = hits[0];
  let n = null;
  if (h.face) n = h.face.normal.clone().transformDirection(h.object.matrixWorld).normalize();
  return { point: h.point.clone(), normal: n };
}

export const VIEW_DIRS = { front: [0, 0, 1], back: [0, 0, -1], left: [-1, 0, 0], right: [1, 0, 0], top: [0, 1, 0], bottom: [0, -1, 0], iso: [0.7, 0.55, 1] };

// Derive all 6 orthographic views (+iso) as { d, up } from a chosen front-face
// normal and bottom-face normal, so every named view renders the model straight & square.
export function viewsFromFrontBottom(frontN, bottomN) {
  const a = (v) => [v.x, v.y, v.z];
  const up = new THREE.Vector3(bottomN[0], bottomN[1], bottomN[2]).multiplyScalar(-1).normalize();
  let front = new THREE.Vector3(frontN[0], frontN[1], frontN[2]);
  front.sub(up.clone().multiplyScalar(front.dot(up)));            // orthogonalise vs up
  if (front.lengthSq() < 1e-6) front = new THREE.Vector3(frontN[0], frontN[1], frontN[2]);
  front.normalize();
  const right = new THREE.Vector3().crossVectors(up, front).normalize();
  const iso = front.clone().add(up.clone().multiplyScalar(0.8)).add(right.clone().multiplyScalar(0.8)).normalize();
  return {
    front: { d: a(front), up: a(up) },
    back: { d: a(front.clone().negate()), up: a(up) },
    right: { d: a(right), up: a(up) },
    left: { d: a(right.clone().negate()), up: a(up) },
    top: { d: a(up), up: a(front.clone().negate()) },
    bottom: { d: a(up.clone().negate()), up: a(front) },
    iso: { d: a(iso), up: a(up) },
  };
}

// Place the camera for a view. `view` is either [x,y,z] (direction only, world-up used)
// or { d:[x,y,z], up:[x,y,z] } to also control screen-up so the model sits perfectly straight.
export function applyView(engine, view, factor = 2.2) {
  const c = engine.center || new THREE.Vector3();
  const d = (engine.radius || 50) * factor;
  const arr = Array.isArray(view) ? view : view.d;
  const v = new THREE.Vector3(arr[0], arr[1], arr[2]).normalize();
  let up;
  if (!Array.isArray(view) && view.up) {
    up = new THREE.Vector3(view.up[0], view.up[1], view.up[2]).normalize();
  } else {
    up = new THREE.Vector3(0, 1, 0);
    if (Math.abs(v.y) > 0.95) up.set(0, 0, v.y > 0 ? -1 : 1);
  }
  engine.camera.up.copy(up);
  engine.camera.position.copy(c).addScaledVector(v, d);
  engine.controls.target.copy(c); engine.camera.lookAt(c); engine.controls.update();
}

// Raycast to a triangle. Returns {point, normal(world), highlight(Mesh)} or null.
export function pickFace(engine, ev) {
  const ray = new THREE.Raycaster(); ray.setFromCamera(_mouseFromEvent(engine, ev), engine.camera);
  const hits = ray.intersectObjects(_visibleMeshes(engine), true);
  if (!hits.length || !hits[0].face) return null;
  const it = hits[0], obj = it.object, face = it.face;
  const n = face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(obj.matrixWorld)).normalize();
  const pos = obj.geometry.attributes.position;
  const a = new THREE.Vector3().fromBufferAttribute(pos, face.a).applyMatrix4(obj.matrixWorld);
  const b = new THREE.Vector3().fromBufferAttribute(pos, face.b).applyMatrix4(obj.matrixWorld);
  const cc = new THREE.Vector3().fromBufferAttribute(pos, face.c).applyMatrix4(obj.matrixWorld);
  const g = new THREE.BufferGeometry().setFromPoints([a, b, cc]);
  const mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xdb960d, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthTest: false }));
  mesh.position.addScaledVector(n, (engine.radius || 50) * 0.003);
  mesh.renderOrder = 999;
  return { point: it.point.clone(), normal: [n.x, n.y, n.z], highlight: mesh };
}

// Raycast to a flat face; gather all coplanar triangles of the hit mesh.
// Returns { partId, normal[3], center[3], verts:[Vector3], highlight(Mesh) } or null.
export function faceRegion(engine, ev, color = 0x2e86de) {
  const ray = new THREE.Raycaster(); ray.setFromCamera(_mouseFromEvent(engine, ev), engine.camera);
  const hits = ray.intersectObjects(_visibleMeshes(engine), true);
  if (!hits.length || !hits[0].face) return null;
  const it = hits[0], obj = it.object;
  const nWorld = it.face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(obj.matrixWorld)).normalize();
  let partId = null, o = obj;
  while (o) { const f = engine.parts.find((p) => p.outer === o); if (f) { partId = f.id; break; } o = o.parent; }
  const pos = obj.geometry.attributes.position, idx = obj.geometry.index, mw = obj.matrixWorld;
  const planeD = nWorld.dot(it.point);
  const tol = (engine.radius || 50) * 0.004;
  const verts = [];
  const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3(), e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), tn = new THREE.Vector3();
  const triCount = idx ? idx.count / 3 : pos.count / 3;
  for (let i = 0; i < triCount; i++) {
    const a = idx ? idx.getX(i * 3) : i * 3, b = idx ? idx.getX(i * 3 + 1) : i * 3 + 1, c = idx ? idx.getX(i * 3 + 2) : i * 3 + 2;
    va.fromBufferAttribute(pos, a).applyMatrix4(mw); vb.fromBufferAttribute(pos, b).applyMatrix4(mw); vc.fromBufferAttribute(pos, c).applyMatrix4(mw);
    tn.copy(e1.subVectors(vb, va).cross(e2.subVectors(vc, va))).normalize();
    if (tn.dot(nWorld) < 0.985) continue;
    if (Math.abs(nWorld.dot(va) - planeD) > tol) continue;
    verts.push(va.clone(), vb.clone(), vc.clone());
  }
  if (!verts.length) verts.push(it.point.clone());
  const center = new THREE.Vector3(); verts.forEach((v) => center.add(v)); center.multiplyScalar(1 / verts.length);
  const g = new THREE.BufferGeometry().setFromPoints(verts);
  const mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthTest: false }));
  mesh.position.addScaledVector(nWorld, (engine.radius || 50) * 0.003); mesh.renderOrder = 999;
  return { partId, normal: [nWorld.x, nWorld.y, nWorld.z], center: [center.x, center.y, center.z], verts, highlight: mesh };
}

// Compute position+rotation(deg) for part A so its `src` face mates flush against `tgt` face.
// centerA = A's current world origin (outer.position), quatA = A's current quaternion. align: center|left|right|top|bottom.
export function computeMate(src, tgt, centerA, quatA, align = "center") {
  const sN = new THREE.Vector3(src.normal[0], src.normal[1], src.normal[2]).normalize();
  const tN = new THREE.Vector3(tgt.normal[0], tgt.normal[1], tgt.normal[2]).normalize();
  const tC = new THREE.Vector3(tgt.center[0], tgt.center[1], tgt.center[2]);
  const sC = new THREE.Vector3(src.center[0], src.center[1], src.center[2]);
  let qRot = new THREE.Quaternion().setFromUnitVectors(sN, tN.clone().negate());
  const newSrcCenter = centerA.clone().add(sC.clone().sub(centerA).applyQuaternion(qRot));
  const refUp = Math.abs(tN.y) < 0.95 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
  const inRight = new THREE.Vector3().crossVectors(refUp, tN).normalize();
  const inUp = new THREE.Vector3().crossVectors(tN, inRight).normalize();
  const ext = (vertsArr, origin, q, about) => {
    let rMin = Infinity, rMax = -Infinity, uMin = Infinity, uMax = -Infinity;
    vertsArr.forEach((v0) => {
      let p = v0;
      if (q) p = about.clone().add(v0.clone().sub(about).applyQuaternion(q));
      const dR = p.clone().sub(origin).dot(inRight), dU = p.clone().sub(origin).dot(inUp);
      rMin = Math.min(rMin, dR); rMax = Math.max(rMax, dR); uMin = Math.min(uMin, dU); uMax = Math.max(uMax, dU);
    });
    return { rMin, rMax, uMin, uMax };
  };
  const tExt = ext(tgt.verts, tC);
  const aExt = ext(src.verts, newSrcCenter, qRot, centerA);
  let offR = (tExt.rMin + tExt.rMax) / 2 - (aExt.rMin + aExt.rMax) / 2;
  let offU = (tExt.uMin + tExt.uMax) / 2 - (aExt.uMin + aExt.uMax) / 2;
  if (align === "left") offR = tExt.rMin - aExt.rMin;
  if (align === "right") offR = tExt.rMax - aExt.rMax;
  if (align === "top") offU = tExt.uMax - aExt.uMax;
  if (align === "bottom") offU = tExt.uMin - aExt.uMin;
  const desired = tC.clone().addScaledVector(inRight, offR).addScaledVector(inUp, offU);
  const newPos = centerA.clone().add(desired.sub(newSrcCenter));
  const eul = new THREE.Euler().setFromQuaternion(qRot.clone().multiply(quatA));
  const R2D = THREE.MathUtils.radToDeg;
  return { position: [newPos.x, newPos.y, newPos.z], rotation: [R2D(eul.x), R2D(eul.y), R2D(eul.z)] };
}

// Project a world point to viewport pixels. Returns {x, y, visible}
export function projectPoint(engine, x, y, z) {
  const v = new THREE.Vector3(x, y, z).project(engine.camera);
  const rect = engine.renderer.domElement.getBoundingClientRect();
  return { x: (v.x * 0.5 + 0.5) * rect.width, y: (-v.y * 0.5 + 0.5) * rect.height, visible: v.z < 1 };
}

export function fetchModel(url, ext) {
  return fetch(url).then((r) => { if (!r.ok) throw new Error("model"); return ext === "obj" ? r.text() : r.arrayBuffer(); });
}


// Record the full step sequence to a downloadable WebM video by playing the
// same cinematic camera/part tweens used in the viewer while capturing the canvas.
export function recordSequence(engine, config, opts = {}) {
  const { transitionMs = 1800, holdMs = 1600, introMs = 1000, fps = 30, onProgress } = opts;
  const canvas = engine.renderer.domElement;
  if (!canvas.captureStream || typeof window.MediaRecorder === "undefined") return Promise.reject(new Error("no-capture"));
  const stream = canvas.captureStream(fps);
  const pick = [
    "video/mp4;codecs=avc1.640028",
    "video/mp4;codecs=avc1.42E01E",
    "video/mp4;codecs=h264",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ].find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
  const outType = pick.split(";")[0];
  const ext = outType === "video/mp4" ? "mp4" : "webm";
  const chunks = [];
  const rec = new MediaRecorder(stream, { mimeType: pick, videoBitsPerSecond: 12000000 });
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  const done = new Promise((resolve) => { rec.onstop = () => resolve({ blob: new Blob(chunks, { type: outType }), ext }); });
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const tween = (cam, eff, dur) => new Promise((r) => tweenTo(engine, cam, eff, dur, r));
  const steps = config.steps || [];
  (async () => {
    rec.start(200);
    for (let i = 0; i < steps.length; i++) {
      const st = steps[i];
      const eff = {};
      (config.parts || []).forEach((p) => {
        const t = (st.transforms && st.transforms[p.id]) || p.base || {};
        eff[p.id] = { ...t, visible: !(st.hidden && st.hidden[p.id]) };
        const ep = engine.parts.find((x) => x.id === p.id);
        if (ep) ep.mat.color.set((st.colors && st.colors[p.id]) || p.color || "#d9d9d9");
      });
      renderExtras(engine, st.dims || []);
      await tween(st.cam, eff, i === 0 ? introMs : transitionMs);
      onProgress && onProgress(i + 1, steps.length);
      await wait(holdMs);
    }
    await wait(300);
    rec.stop();
  })();
  return done;
}


// ---------------------------------------------------------------------------
// Indicators (arrows / rotate / point / warning / check) + mounting surfaces
// ---------------------------------------------------------------------------
export const ANNO_COLORS = [
  { id: "gold", hex: "#DB960D" },
  { id: "ink", hex: "#232323" },
  { id: "red", hex: "#C0392B" },
  { id: "green", hex: "#2C6E49" },
];
export const ANNO_TYPES = ["arrow", "rotate", "point", "warning", "check"];
export const SURFACE_KINDS = ["wall", "ceiling", "desk"];

export function spanOf(engine) {
  const b = engine && engine._box;
  return b ? b.getSize(new THREE.Vector3()).length() : 1;
}

function _labelSprite(text, unit, accent) {
  const fs = 40, padX = 26, padY = 16, bw = 6;
  const c = document.createElement("canvas");
  const ctx = c.getContext("2d");
  ctx.font = `bold ${fs}px Arial`;
  const tw = Math.ceil(ctx.measureText(text).width);
  c.width = tw + padX * 2; c.height = fs + padY * 2;
  ctx.font = `bold ${fs}px Arial`;
  const w = c.width, h = c.height, r = h / 2;
  const rp = (x0, y0, x1, y1, rr) => { ctx.beginPath(); ctx.moveTo(x0 + rr, y0); ctx.arcTo(x1, y0, x1, y1, rr); ctx.arcTo(x1, y1, x0, y1, rr); ctx.arcTo(x0, y1, x0, y0, rr); ctx.arcTo(x0, y0, x1, y0, rr); ctx.closePath(); };
  ctx.fillStyle = "rgba(26,26,26,0.95)"; rp(0, 0, w, h, r); ctx.fill();
  ctx.lineWidth = bw; ctx.strokeStyle = accent || "#DB960D"; rp(bw / 2, bw / 2, w - bw / 2, h - bw / 2, r - bw / 2); ctx.stroke();
  ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(text, w / 2, h / 2 + 2);
  const tex = new THREE.CanvasTexture(c); tex.anisotropy = 4;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sp = new THREE.Sprite(mat);
  const hgt = unit * 0.5;
  sp.scale.set(hgt * (w / h), hgt, 1);
  return sp;
}

function _symbolSprite(sym, bg, unit) {
  const s = 256, c = document.createElement("canvas"); c.width = c.height = s;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "rgba(0,0,0,0.28)"; ctx.beginPath(); ctx.arc(s / 2, s * 0.54, s * 0.44, 0, Math.PI * 2); ctx.fill(); // soft shadow
  ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(s / 2, s / 2, s * 0.44, 0, Math.PI * 2); ctx.fill();             // white ring
  ctx.fillStyle = bg; ctx.beginPath(); ctx.arc(s / 2, s / 2, s * 0.37, 0, Math.PI * 2); ctx.fill();                  // coloured disc
  ctx.fillStyle = "#fff"; ctx.font = `bold ${Math.round(s * 0.5)}px Arial`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(sym, s / 2, s * 0.54);
  const tex = new THREE.CanvasTexture(c); tex.anisotropy = 4;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sp = new THREE.Sprite(mat); sp.scale.setScalar(unit);
  return sp;
}

export function makeAnnotation(anno, span) {
  const g = new THREE.Group();
  const hex = (ANNO_COLORS.find((c) => c.id === anno.color) || {}).hex || anno.color || "#DB960D";
  const col = new THREE.Color(hex);
  const unit = Math.max(0.001, span * 0.18 * (anno.scale || 1));
  const mat = new THREE.MeshStandardMaterial({ color: col, metalness: 0.25, roughness: 0.35, emissive: col.clone().multiplyScalar(0.22) });
  const dark = new THREE.MeshStandardMaterial({ color: col.clone().multiplyScalar(0.7), metalness: 0.25, roughness: 0.4 });
  if (anno.type === "arrow") {
    const shaftLen = unit * 0.6, headLen = unit * 0.4, r = unit * 0.05;
    const tail = new THREE.Mesh(new THREE.SphereGeometry(r * 1.6, 18, 18), mat);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r, shaftLen, 20), mat);
    shaft.position.y = shaftLen / 2;
    const collar = new THREE.Mesh(new THREE.CylinderGeometry(r * 2.6, r * 2.6, unit * 0.03, 22), dark);
    collar.position.y = shaftLen;
    const head = new THREE.Mesh(new THREE.ConeGeometry(r * 2.7, headLen, 26), mat);
    head.position.y = shaftLen + headLen / 2;
    g.add(tail, shaft, collar, head);
  } else if (anno.type === "rotate") {
    const R = unit * 0.44, tube = unit * 0.05, arc = Math.PI * 1.5;
    const torus = new THREE.Mesh(new THREE.TorusGeometry(R, tube, 18, 72, arc), mat);
    const hub = new THREE.Mesh(new THREE.SphereGeometry(tube * 1.3, 16, 16), dark);
    g.add(torus, hub);
    const ex = Math.cos(arc) * R, ey = Math.sin(arc) * R;
    const head = new THREE.Mesh(new THREE.ConeGeometry(tube * 3.4, unit * 0.28, 22), mat);
    head.position.set(ex, ey, 0); head.rotation.z = arc + Math.PI / 2;
    const sx = R, sy = 0;
    const startCap = new THREE.Mesh(new THREE.SphereGeometry(tube * 1.15, 14, 14), mat);
    startCap.position.set(sx, sy, 0);
    g.add(head, startCap);
  } else if (anno.type === "point") {
    const outer = new THREE.Mesh(new THREE.TorusGeometry(unit * 0.36, unit * 0.045, 16, 48), mat);
    const mid = new THREE.Mesh(new THREE.TorusGeometry(unit * 0.22, unit * 0.03, 14, 40), dark);
    const disc = new THREE.Mesh(new THREE.CircleGeometry(unit * 0.12, 28), mat);
    disc.position.z = 0.001;
    g.add(outer, mid, disc);
  } else if (anno.type === "warning") {
    g.add(_symbolSprite("!", "#C0392B", unit * 0.9));
  } else if (anno.type === "check") {
    g.add(_symbolSprite("✓", "#2C6E49", unit * 0.9));
  }
  if (anno.label) {
    const lbl = _labelSprite(anno.label, unit, hex);
    lbl.position.y = (anno.type === "arrow" ? unit * 1.15 : unit * 0.68);
    g.add(lbl);
  }
  // anchor + offset pulled along the picked surface normal (fallback: world up)
  let n;
  if (anno.nx !== undefined && (anno.nx || anno.ny || anno.nz)) n = new THREE.Vector3(anno.nx, anno.ny, anno.nz).normalize();
  else n = new THREE.Vector3(0, 1, 0);
  const off = anno.offset || 0;
  g.position.set((anno.x || 0) + n.x * off, (anno.y || 0) + n.y * off, (anno.z || 0) + n.z * off);
  g.rotation.set(THREE.MathUtils.degToRad(anno.rx || 0), THREE.MathUtils.degToRad(anno.ry || 0), THREE.MathUtils.degToRad(anno.rz || 0));
  g.renderOrder = 999;
  g.userData.extra = true;
  g.userData.anno = true;
  g.userData.phase = ((anno.x || 0) + (anno.y || 0)) % 6.28;
  return g;
}

export function makeSurface(surf, span) {
  const s = span || 1;
  const w = (surf.w || 1.6) * s, h = (surf.h || 1.6) * s;
  const geo = new THREE.PlaneGeometry(w, h);
  const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(surf.color || "#dcdcdc"), roughness: 0.96, metalness: 0.0, side: THREE.DoubleSide });
  const m = new THREE.Mesh(geo, mat);
  m.receiveShadow = true;
  if (surf.kind === "ceiling") m.rotation.x = Math.PI / 2;
  else if (surf.kind === "desk" || surf.kind === "floor") m.rotation.x = -Math.PI / 2;
  // "wall" keeps the default XY plane (vertical)
  m.position.set(surf.x || 0, surf.y || 0, surf.z || 0);
  m.userData.extra = true;
  return m;
}

// Rebuild the extra scene objects (surfaces + current-step indicators + dimensions)
export function renderExtras(engine, dims) {
  if (!engine) return;
  const span = spanOf(engine);
  (engine._extras || []).forEach((o) => {
    engine.scene.remove(o);
    o.traverse && o.traverse((c) => { if (c.geometry) c.geometry.dispose(); if (c.material && c.material.map) c.material.map.dispose(); });
  });
  engine._extras = [];
  (dims || []).forEach((d) => { const gr = makeDimension(d, span); engine.scene.add(gr); engine._extras.push(gr); });
}

// ---------------------------------------------------------------------------
// CAD-style dimensions with snap points
// ---------------------------------------------------------------------------
const _snapCands = (engine, ev) => {
  const ray = new THREE.Raycaster(); ray.setFromCamera(_mouseFromEvent(engine, ev), engine.camera);
  const hits = ray.intersectObjects(_visibleMeshes(engine), true);
  if (!hits.length || !hits[0].face) return null;
  const it = hits[0], obj = it.object, face = it.face, mw = obj.matrixWorld, pos = obj.geometry.attributes.position;
  const va = new THREE.Vector3().fromBufferAttribute(pos, face.a).applyMatrix4(mw);
  const vb = new THREE.Vector3().fromBufferAttribute(pos, face.b).applyMatrix4(mw);
  const vc = new THREE.Vector3().fromBufferAttribute(pos, face.c).applyMatrix4(mw);
  return { it, cands: [
    { p: va, kind: "vertex" }, { p: vb, kind: "vertex" }, { p: vc, kind: "vertex" },
    { p: va.clone().add(vb).multiplyScalar(0.5), kind: "edge" },
    { p: vb.clone().add(vc).multiplyScalar(0.5), kind: "edge" },
    { p: vc.clone().add(va).multiplyScalar(0.5), kind: "edge" },
    { p: va.clone().add(vb).add(vc).multiplyScalar(1 / 3), kind: "face" },
  ] };
};

// Best snap point (vertex > edge midpoint > face centre) nearest to the cursor
export function snapPoint(engine, ev) {
  const r = _snapCands(engine, ev);
  if (!r) return null;
  const rect = engine.mount.getBoundingClientRect();
  const mx = ev.clientX - rect.left, my = ev.clientY - rect.top;
  const toScreen = (v) => { const p = v.clone().project(engine.camera); return { x: (p.x * 0.5 + 0.5) * rect.width, y: (-p.y * 0.5 + 0.5) * rect.height }; };
  let best = null, bestD = Infinity; const thr = 30;
  for (const c of r.cands) { const s = toScreen(c.p); const d = Math.hypot(s.x - mx, s.y - my); const pr = c.kind === "vertex" ? 0 : (c.kind === "edge" ? 7 : 14); if (d < thr && d + pr < bestD) { bestD = d + pr; best = c; } }
  if (!best) best = { p: r.it.point.clone(), kind: "point" };
  return { point: [best.p.x, best.p.y, best.p.z], kind: best.kind };
}

// Circle/arc from a flat circular face: centre + radius (rim = farthest coplanar vert)
export function circleFromFace(engine, ev) {
  const fr = faceRegion(engine, ev);
  if (!fr) return null;
  const c = new THREE.Vector3(fr.center[0], fr.center[1], fr.center[2]);
  let rad = 0; fr.verts.forEach((v) => { rad = Math.max(rad, v.distanceTo(c)); });
  return { center: fr.center, radius: rad, normal: fr.normal };
}

export function showSnapMarker(engine, point) {
  if (!engine._snap) { const m = new THREE.Mesh(new THREE.SphereGeometry(1, 18, 18), new THREE.MeshBasicMaterial({ color: 0x1f6feb, depthTest: false })); m.renderOrder = 1000; engine._snap = m; engine.scene.add(m); }
  engine._snap.scale.setScalar((engine.radius || 50) * 0.02);
  engine._snap.position.set(point[0], point[1], point[2]); engine._snap.visible = true;
}
export function hideSnapMarker(engine) { if (engine && engine._snap) engine._snap.visible = false; }

function _dimArrow(g, at, dir, size, mat) {
  const cone = new THREE.Mesh(new THREE.ConeGeometry(size * 0.4, size, 16), mat);
  cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
  cone.position.copy(at).addScaledVector(dir, -size / 2);
  g.add(cone);
}
function _dimLine(g, A, B, lineMat, arrMat, span) {
  const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints([A, B]), lineMat);
  g.add(line);
  const d = new THREE.Vector3().subVectors(B, A).normalize();
  const size = span * 0.03;
  _dimArrow(g, B, d, size, arrMat);
  _dimArrow(g, A, d.clone().negate(), size, arrMat);
}
function _dimLabel(g, pos, text, span, accent) {
  const sp = _labelSprite(text, span * 0.06, accent);
  sp.position.copy(pos);
  g.add(sp);
}

function _perp(dir) {
  const up = Math.abs(dir.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  return new THREE.Vector3().crossVectors(dir, up).normalize();
}

export function offsetFromCursor(engine, ev, p1, p2, kind) {
  const P1 = new THREE.Vector3(p1[0], p1[1], p1[2]);
  let P2 = new THREE.Vector3(p2[0], p2[1], p2[2]);
  if (kind === "x" || kind === "y" || kind === "z") { const q = P1.clone(); q[kind] = P2[kind]; P2 = q; }
  const M = P1.clone().add(P2).multiplyScalar(0.5);
  const dir = new THREE.Vector3().subVectors(P2, P1).normalize();
  const o = _perp(dir);
  const ray = new THREE.Raycaster(); ray.setFromCamera(_mouseFromEvent(engine, ev), engine.camera);
  const n = engine.camera.getWorldDirection(new THREE.Vector3());
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, M);
  const hit = new THREE.Vector3();
  const sp = spanOf(engine) * 0.10;
  if (!ray.ray.intersectPlane(plane, hit)) return [o.x * sp, o.y * sp, o.z * sp];
  const rel = new THREE.Vector3().subVectors(hit, M);
  const perp = rel.sub(rel.clone().projectOnVector(dir));
  return [perp.x, perp.y, perp.z];
}

export function makeDimension(dim, span) {
  const g = new THREE.Group();
  if (!dim || (dim.kind === "dia" ? !dim.center : (!dim.p1 || !dim.p2))) return g;
  const hex = "#232323";
  const col = new THREE.Color(hex);
  const lineMat = new THREE.LineBasicMaterial({ color: col, depthTest: false });
  const arrMat = new THREE.MeshBasicMaterial({ color: col, depthTest: false });
  const gap = span * 0.012, over = span * 0.02;
  if (dim.kind === "dia") {
    const c = new THREE.Vector3(dim.center[0], dim.center[1], dim.center[2]);
    const n = new THREE.Vector3(...(dim.normal || [0, 0, 1])).normalize();
    const ref = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(n, ref).normalize();
    const a = c.clone().addScaledVector(u, -dim.radius), b = c.clone().addScaledVector(u, dim.radius);
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b]), lineMat));
    _dimArrow(g, a, u.clone().negate(), span * 0.03, arrMat);
    _dimArrow(g, b, u, span * 0.03, arrMat);
    _dimLabel(g, c, "\u2300 " + (dim.radius * 2).toFixed(1) + " mm", span, hex);
  } else {
    const P1 = new THREE.Vector3(dim.p1[0], dim.p1[1], dim.p1[2]);
    let P2 = new THREE.Vector3(dim.p2[0], dim.p2[1], dim.p2[2]);
    let value;
    if (dim.kind === "x" || dim.kind === "y" || dim.kind === "z") {
      const k = dim.kind; const d = P2[k] - P1[k]; P2 = P1.clone(); P2[k] = P1[k] + d; value = Math.abs(d);
    } else { value = P1.distanceTo(P2); }
    const dir = new THREE.Vector3().subVectors(P2, P1).normalize();
    let offVec;
    if (Array.isArray(dim.offset)) offVec = new THREE.Vector3(dim.offset[0], dim.offset[1], dim.offset[2]);
    else offVec = _perp(dir).multiplyScalar(dim.offset != null ? dim.offset : span * 0.10);
    const o = offVec.length() > 1e-6 ? offVec.clone().normalize() : _perp(dir);   // unit toward the dimension line
    const A2 = P1.clone().add(offVec), B2 = P2.clone().add(offVec);
    // witness/extension lines (small gap from the feature, slight over-run)
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([P1.clone().addScaledVector(o, gap), A2.clone().addScaledVector(o, over)]), lineMat));
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([P2.clone().addScaledVector(o, gap), B2.clone().addScaledVector(o, over)]), lineMat));
    // dimension line + arrowheads pointing outward to the witness lines
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([A2, B2]), lineMat));
    const along = new THREE.Vector3().subVectors(B2, A2).normalize();
    _dimArrow(g, A2, along.clone().negate(), span * 0.03, arrMat);
    _dimArrow(g, B2, along, span * 0.03, arrMat);
    _dimLabel(g, A2.clone().add(B2).multiplyScalar(0.5), value.toFixed(1) + " mm", span, hex);
  }
  g.renderOrder = 999; g.userData.extra = true;
  g.traverse((o) => { o.renderOrder = 999; if (o.material) { o.material.depthTest = false; o.material.depthWrite = false; o.material.transparent = true; } });
  return g;
}

// live preview of the dimension while picking the second point
export function showDimPreview(engine, dim) {
  clearDimPreview(engine);
  const g = makeDimension(dim, spanOf(engine));
  g.traverse((o) => { if (o.material) { o.material.opacity = 0.6; o.material.transparent = true; } });
  engine._dimPreview = g; engine.scene.add(g);
}
export function clearDimPreview(engine) {
  if (engine && engine._dimPreview) { engine.scene.remove(engine._dimPreview); engine._dimPreview.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material && o.material.map) o.material.map.dispose(); }); engine._dimPreview = null; }
}



// ---------------------------------------------------------------------------
// Fusion 360-style ViewCube (corner gizmo synced to the camera, clickable faces)
// ---------------------------------------------------------------------------
function _cubeFaceTexture(label) {
  const s = 256, c = document.createElement("canvas"); c.width = c.height = s;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#f3f1ec"; ctx.fillRect(0, 0, s, s);
  ctx.strokeStyle = "#c9c4b8"; ctx.lineWidth = 10; ctx.strokeRect(5, 5, s - 10, s - 10);
  ctx.fillStyle = "#3a3a3a"; ctx.font = `600 ${Math.round(s * 0.17)}px Arial`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(label, s / 2, s / 2);
  const tex = new THREE.CanvasTexture(c); tex.anisotropy = 4;
  return tex;
}

export function enableViewCube(engine, onPick) {
  if (!engine || engine._gizmo) return;
  const scene = new THREE.Scene();
  const cam = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  cam.position.set(0, 0, 6);
  scene.add(new THREE.AmbientLight(0xffffff, 0.95));
  const dl = new THREE.DirectionalLight(0xffffff, 0.55); dl.position.set(3, 5, 4); scene.add(dl);
  // labels in BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z
  const labels = ["RECHTS", "LINKS", "BOVEN", "ONDER", "VOOR", "ACHTER"];
  const mats = labels.map((l) => new THREE.MeshStandardMaterial({ map: _cubeFaceTexture(l), metalness: 0.0, roughness: 0.85 }));
  const cube = new THREE.Mesh(new THREE.BoxGeometry(2.1, 2.1, 2.1), mats);
  const edges = new THREE.LineSegments(new THREE.EdgesGeometry(cube.geometry), new THREE.LineBasicMaterial({ color: 0x9a948a }));
  cube.add(edges);
  const axes = new THREE.AxesHelper(1.85); axes.position.set(-1.05, -1.05, -1.05); cube.add(axes);
  scene.add(cube);

  const gz = { scene, camera: cam, cube, dist: 6, size: 108, margin: 12, onPick: onPick || (() => {}) };
  engine._gizmo = gz;

  const ray = new THREE.Raycaster();
  const onClick = (e) => {
    const rect = engine.mount.getBoundingClientRect();
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const s = gz.size, m = gz.margin;
    const x0 = rect.width - s - m, y0 = m;
    if (px < x0 || px > x0 + s || py < y0 || py > y0 + s) return;
    const nx = ((px - x0) / s) * 2 - 1, ny = -(((py - y0) / s) * 2 - 1);
    ray.setFromCamera({ x: nx, y: ny }, gz.camera);
    const hit = ray.intersectObject(gz.cube, false)[0];
    if (!hit || !hit.face) return;
    const n = hit.face.normal, ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z);
    let name;
    if (ax >= ay && ax >= az) name = n.x > 0 ? "right" : "left";
    else if (ay >= ax && ay >= az) name = n.y > 0 ? "top" : "bottom";
    else name = n.z > 0 ? "front" : "back";
    e.stopPropagation(); e.preventDefault();
    gz.onPick(name);
  };
  engine.renderer.domElement.addEventListener("pointerdown", onClick, true);
  gz._onClick = onClick;
}

export function disableViewCube(engine) {
  const gz = engine && engine._gizmo;
  if (!gz) return;  engine.renderer.domElement.removeEventListener("pointerdown", gz._onClick, true);
  gz.cube.material.forEach((m) => { if (m.map) m.map.dispose(); m.dispose(); });
  gz.cube.geometry.dispose();
  engine._gizmo = null;
}

const _gzDir = new THREE.Vector3();

// Orient the ViewCube so its labelled faces line up with the model's view-frame
export function setViewCubeFrame(engine, views) {
  const gz = engine && engine._gizmo;
  if (!gz) return;
  const vf = views && views.front;
  let F, U;
  if (vf && vf.d) { F = new THREE.Vector3(vf.d[0], vf.d[1], vf.d[2]).normalize(); U = new THREE.Vector3((vf.up || [0, 1, 0])[0], (vf.up || [0, 1, 0])[1], (vf.up || [0, 1, 0])[2]).normalize(); }
  else { F = new THREE.Vector3(0, 0, 1); U = new THREE.Vector3(0, 1, 0); }
  const R = new THREE.Vector3().crossVectors(U, F).normalize();
  const U2 = new THREE.Vector3().crossVectors(F, R).normalize();
  const m = new THREE.Matrix4().makeBasis(R, U2, F);
  gz.cube.quaternion.setFromRotationMatrix(m);
}

function _renderGizmo(engine) {
  const gz = engine._gizmo, r = engine.renderer;
  _gzDir.subVectors(engine.camera.position, engine.controls.target).normalize();
  gz.camera.position.copy(_gzDir).multiplyScalar(gz.dist);
  gz.camera.up.copy(engine.camera.up);
  gz.camera.lookAt(0, 0, 0);
  const W = engine.mount.clientWidth, H = engine.mount.clientHeight, s = gz.size, m = gz.margin;
  r.autoClear = false;
  r.setScissorTest(true);
  r.setScissor(W - s - m, H - s - m, s, s);
  r.setViewport(W - s - m, H - s - m, s, s);
  r.clearDepth();
  r.render(gz.scene, gz.camera);
  r.setScissorTest(false);
  r.setViewport(0, 0, W, H);
  r.autoClear = true;
}

// Tilt the view up/down around the horizontal screen axis (ViewCube chevrons)
export function tiltView(engine, deg) {
  const cam = engine.camera, t = engine.controls.target;
  const offset = new THREE.Vector3().subVectors(cam.position, t);
  const right = new THREE.Vector3().crossVectors(cam.up, offset).normalize();
  if (right.lengthSq() < 1e-6) right.set(1, 0, 0);
  const q = new THREE.Quaternion().setFromAxisAngle(right, THREE.MathUtils.degToRad(deg));
  offset.applyQuaternion(q);
  cam.up.applyQuaternion(q).normalize();
  cam.position.copy(t).add(offset);
  cam.lookAt(t);
  engine.controls.update && engine.controls.update();
}

// Render the ViewCube gizmo into the top-right corner. Call inside a custom
// animate loop (createEngine does this automatically; standalone engines call it).
export function renderViewCubeGizmo(engine) {
  if (engine && engine._gizmo) _renderGizmo(engine);
}
